import React, { useState, useEffect } from 'react';
import { api, apiUrl, hoy } from '../utils/api';
import { Spinner } from '../components/UI';
import { useDictado } from '../hooks/useDictado';

export default function CerrarOrden({ idOrden, onCerrada, onCancelar }) {
  const [orden, setOrden] = useState(null);
  const [cargando, setCargando] = useState(true);

  const [km, setKm] = useState('');
  const [fechaCierre, setFechaCierre] = useState(hoy());
  const [escaneo, setEscaneo] = useState('');
  const [bateria, setBateria] = useState('');
  const [obs, setObs] = useState('');

  const [generandoObs, setGenerandoObs] = useState(false);
  const [puliendo, setPuliendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [avisoRepuestos, setAvisoRepuestos] = useState(false);

  const { grabando, soportado, iniciar, detener } = useDictado();

  useEffect(() => {
    api(`/ordenes/${idOrden}?incluir_costos_internos=true`).then(o => {
      setOrden(o);
      setKm(o.kilometraje || '');
      setEscaneo(o.escaneo_resultado || '');
      setBateria(o.bateria_estado || '');
      setObs(o.observaciones || '');
      setCargando(false);
    });
  }, [idOrden]);

  const generarObs = async () => {
    if (!escaneo && !bateria) return;
    setGenerandoObs(true);
    try {
      const res = await api('/importar/generar-observaciones', {
        method: 'POST',
        body: JSON.stringify({
          escaneo_resultado: escaneo || null, bateria_estado: bateria || null,
          marca: orden?.marca, modelo: orden?.modelo,
        })
      });
      setObs(res.observaciones);
    } catch { }
    setGenerandoObs(false);
  };

  const toggleDictado = () => {
    if (grabando) { detener(); return; }
    iniciar(async (textoCrudo) => {
      if (!textoCrudo) return;
      setPuliendo(true);
      try {
        const res = await api('/importar/pulir-dictado', {
          method: 'POST', body: JSON.stringify({ texto_crudo: textoCrudo })
        });
        setObs(prev => prev ? `${prev}\n${res.texto}` : res.texto);
      } catch {
        setObs(prev => prev ? `${prev}\n${textoCrudo}` : textoCrudo);
      }
      setPuliendo(false);
    });
  };

  const hayRepuestosSinCodigo = () => {
    if (!orden) return false;
    return orden.paquetes.some(p => p.repuestos.some(r => !r.codigo));
  };

  const intentarCerrar = () => {
    if (hayRepuestosSinCodigo() && !avisoRepuestos) {
      setAvisoRepuestos(true);
      return;
    }
    cerrarDeVerdad();
  };

  const cerrarDeVerdad = async () => {
    setGuardando(true); setError('');
    try {
      await api(`/ordenes/${idOrden}/cerrar`, {
        method: 'POST',
        body: JSON.stringify({
          kilometraje: parseInt(km),
          fecha_cierre: fechaCierre,
          escaneo_resultado: escaneo || null,
          bateria_estado: bateria || null,
          observaciones: obs,
          paquetes: orden.paquetes.map(p => ({
            nombre: p.nombre,
            precio: p.precio,
            tareas: p.tareas.map(t => ({ descripcion: t.descripcion, orden_display: t.orden_display })),
            repuestos: p.repuestos.map(r => ({
              nombre: r.nombre, detalle: r.detalle, codigo: r.codigo,
              costo_taller: r.costo_taller, proveedor: r.proveedor, orden_display: r.orden_display
            })),
          })),
        })
      });
      onCerrada();
    } catch (e) {
      setError(e.message);
    }
    setGuardando(false);
  };

  if (cargando) return <div className="page-content"><Spinner texto="Cargando orden..." /></div>;
  if (!orden) return null;

  const puedeCerrar = km && parseInt(km) > 0;

  return (
    <div className="page-content">
      <div className="sec-header">
        <h2 className="page-title" style={{ marginBottom: 0 }}>Cerrar orden {orden.id}</h2>
        <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Volver</button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="resumen-grid">
          <div className="resumen-card"><div className="label">Cliente</div><div className="valor">{orden.nombre_cliente}</div></div>
          <div className="resumen-card"><div className="label">Vehículo</div><div className="valor">{orden.marca} {orden.modelo}</div></div>
          <div className="resumen-card"><div className="label">Matrícula</div><div className="valor">{orden.matricula || '—'}</div></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.6rem' }}>Servicios realizados</p>
        {orden.paquetes.map((p, i) => (
          <div key={i} style={{ marginBottom: '.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 500 }}>{p.nombre}</span>
              <span>${p.precio.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="form-grid-2">
          <div className="form-group" style={{ maxWidth: 240 }}>
            <label className="form-label">Kilometraje <span className="req">*</span></label>
            <input className="form-input" type="number" placeholder="Ej: 75000" value={km}
              onChange={e => setKm(e.target.value)} />
          </div>
          <div className="form-group" style={{ maxWidth: 240 }}>
            <label className="form-label">Fecha de cierre</label>
            <input className="form-input" type="date" value={fechaCierre}
              onChange={e => setFechaCierre(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.75rem', color: 'var(--azul)' }}>
          🔌 Escaneo electrónico <span style={{ color: 'var(--gris-4)', fontWeight: 400, fontSize: '.8125rem' }}>(opcional)</span>
        </p>
        <div className="radio-group">
          {[{ val: '', label: 'No aplica' }, { val: 'sin_fallas', label: '✅ Sin fallas' }, { val: 'con_fallas', label: '⚠️ Con fallas' }].map(({ val, label }) => (
            <button key={val || 'none'} className={`radio-btn ${escaneo === val && val ? `selected-${val}` : ''} ${!val && !escaneo ? 'selected-buena' : ''}`}
              onClick={() => setEscaneo(val)}>{label}</button>
          ))}
        </div>
        {escaneo === 'con_fallas' && (
          <p style={{ marginTop: '.6rem', fontSize: '.875rem', color: 'var(--gris-4)', fontStyle: 'italic' }}>
            Se incluirá en la orden: "Ver informe adjunto"
          </p>
        )}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.75rem', color: 'var(--azul)' }}>
          🔋 Batería <span style={{ color: 'var(--gris-4)', fontWeight: 400, fontSize: '.8125rem' }}>(opcional)</span>
        </p>
        <div className="radio-group">
          {[{ val: '', label: 'No aplica' }, { val: 'buena', label: '✅ Buena' }, { val: 'regular', label: '⚠️ Regular' }, { val: 'mala', label: '❌ Mala' }].map(({ val, label }) => (
            <button key={val || 'none'} className={`radio-btn ${bateria === val && val ? `selected-${val}` : ''} ${!val && !bateria ? 'selected-buena' : ''}`}
              onClick={() => setBateria(val)}>{label}</button>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="sec-header" style={{ marginBottom: '.5rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--azul)' }}>📝 Observaciones</p>
          <div style={{ display: 'flex', gap: '.4rem' }}>
            {soportado && (
              <button className={`mic-btn ${grabando ? 'recording' : ''}`} onClick={toggleDictado}>🎤</button>
            )}
            <button className="btn btn-outline btn-sm" disabled={(!escaneo && !bateria) || generandoObs} onClick={generarObs}>
              {generandoObs ? '...' : '✨ Generar con IA'}
            </button>
          </div>
        </div>
        {puliendo && <Spinner texto="Redactando..." />}
        {generandoObs && <Spinner texto="Generando..." />}
        <textarea className="form-textarea" rows={5} value={obs} onChange={e => setObs(e.target.value)} />
      </div>

      {avisoRepuestos && (
        <div className="card" style={{ marginBottom: '1rem', background: '#fff3e0', borderLeft: '3px solid #b45309' }}>
          <p style={{ fontSize: '.875rem', color: '#92400e' }}>
            ⚠️ Hay repuestos sin código registrado. ¿Querés cerrar la orden igual?
          </p>
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.6rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setAvisoRepuestos(false)}>Volver a revisar</button>
            <button className="btn btn-primary btn-sm" onClick={cerrarDeVerdad}>Cerrar igual</button>
          </div>
        </div>
      )}

      {error && <p style={{ color: 'var(--rojo)', marginBottom: '.75rem', fontSize: '.875rem' }}>{error}</p>}
      {guardando && <Spinner texto="Cerrando orden..." />}

      {!avisoRepuestos && (
        <button className="btn btn-primary btn-full" disabled={!puedeCerrar || guardando} onClick={intentarCerrar}>
          ✅ Aceptar y cerrar orden
        </button>
      )}
    </div>
  );
}
